import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(`
  :host { display: contents; }
  [part="base"] { box-sizing: border-box; }
`);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(`
  :host { display: contents; }
  [part="base"] { box-sizing: border-box; }
`);

class RunGridItem extends RunElement {
  #skin = null;
  #colSpan = '';
  #rowSpan = '';
  #colStart = '';
  #colEnd = '';
  #rowStart = '';
  #rowEnd = '';
  #area = '';
  #order = '';
  #subgrid = false;

  #rendering = false;

  static get observedAttributes() {
    return [
      'skin',
      'col-span', 'row-span',
      'col-start', 'col-end',
      'row-start', 'row-end',
      'area', 'order', 'subgrid',
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'skin':      this.skin = newValue; break;
      case 'col-span':  this.colSpan = newValue !== null ? newValue : ''; break;
      case 'row-span':  this.rowSpan = newValue !== null ? Number(newValue) : ''; break;
      case 'col-start': this.colStart = newValue !== null ? Number(newValue) : ''; break;
      case 'col-end':   this.colEnd = newValue !== null ? Number(newValue) : ''; break;
      case 'row-start': this.rowStart = newValue !== null ? Number(newValue) : ''; break;
      case 'row-end':   this.rowEnd = newValue !== null ? Number(newValue) : ''; break;
      case 'area':      this.area = newValue !== null ? newValue : ''; break;
      case 'order':     this.order = newValue !== null ? Number(newValue) : ''; break;
      case 'subgrid':   this.subgrid = newValue !== null; break;
    }
  }

  // ── skin ──────────────────────────────────────────────────────────────────
  get skin() { return this.#skin; }
  set skin(value) {
    const coerced = value === null || value === undefined ? null : String(value);
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    if (coerced !== null) {
      this.setAttribute('skin', coerced);
    } else {
      this.removeAttribute('skin');
    }
    this.requestUpdate();
  }

  // ── colSpan ───────────────────────────────────────────────────────────────
  get colSpan() { return this.#colSpan; }
  set colSpan(value) {
    let coerced;
    if (value === 'full') {
      coerced = 'full';
    } else {
      const n = Number(value);
      coerced = isNaN(n) ? '' : n;
    }
    if (this.#colSpan === coerced) return;
    this.#colSpan = coerced;
    if (coerced !== '') {
      this.setAttribute('col-span', String(coerced));
    } else {
      this.removeAttribute('col-span');
    }
    this.requestUpdate();
  }

  get _resolvedGridColumn() {
    const s = this.#colSpan;
    if (s === 'full') return '1 / -1';
    if (s !== '' && !isNaN(Number(s))) return `span ${s}`;
    return '';
  }

  // ── rowSpan ───────────────────────────────────────────────────────────────
  get rowSpan() { return this.#rowSpan; }
  set rowSpan(value) {
    const n = Number(value);
    const coerced = isNaN(n) ? '' : n;
    if (this.#rowSpan === coerced) return;
    this.#rowSpan = coerced;
    if (coerced !== '') {
      this.setAttribute('row-span', String(coerced));
    } else {
      this.removeAttribute('row-span');
    }
    this.requestUpdate();
  }

  // ── colStart ──────────────────────────────────────────────────────────────
  get colStart() { return this.#colStart; }
  set colStart(value) {
    const n = Number(value);
    const coerced = isNaN(n) ? '' : n;
    if (this.#colStart === coerced) return;
    this.#colStart = coerced;
    if (coerced !== '') {
      this.setAttribute('col-start', String(coerced));
    } else {
      this.removeAttribute('col-start');
    }
    this.requestUpdate();
  }

  // ── colEnd ────────────────────────────────────────────────────────────────
  get colEnd() { return this.#colEnd; }
  set colEnd(value) {
    const n = Number(value);
    const coerced = isNaN(n) ? '' : n;
    if (this.#colEnd === coerced) return;
    this.#colEnd = coerced;
    if (coerced !== '') {
      this.setAttribute('col-end', String(coerced));
    } else {
      this.removeAttribute('col-end');
    }
    this.requestUpdate();
  }

  // ── rowStart ──────────────────────────────────────────────────────────────
  get rowStart() { return this.#rowStart; }
  set rowStart(value) {
    const n = Number(value);
    const coerced = isNaN(n) ? '' : n;
    if (this.#rowStart === coerced) return;
    this.#rowStart = coerced;
    if (coerced !== '') {
      this.setAttribute('row-start', String(coerced));
    } else {
      this.removeAttribute('row-start');
    }
    this.requestUpdate();
  }

  // ── rowEnd ────────────────────────────────────────────────────────────────
  get rowEnd() { return this.#rowEnd; }
  set rowEnd(value) {
    const n = Number(value);
    const coerced = isNaN(n) ? '' : n;
    if (this.#rowEnd === coerced) return;
    this.#rowEnd = coerced;
    if (coerced !== '') {
      this.setAttribute('row-end', String(coerced));
    } else {
      this.removeAttribute('row-end');
    }
    this.requestUpdate();
  }

  // ── area ──────────────────────────────────────────────────────────────────
  get area() { return this.#area; }
  set area(value) {
    const coerced = value == null ? '' : String(value);
    if (this.#area === coerced) return;
    this.#area = coerced;
    if (coerced) { this.setAttribute('area', coerced); } else { this.removeAttribute('area'); }
    this.requestUpdate();
  }

  // ── order ─────────────────────────────────────────────────────────────────
  get order() { return this.#order; }
  set order(value) {
    const n = Number(value);
    const coerced = isNaN(n) ? '' : n;
    if (this.#order === coerced) return;
    this.#order = coerced;
    if (coerced !== '') {
      this.setAttribute('order', String(coerced));
    } else {
      this.removeAttribute('order');
    }
    this.requestUpdate();
  }

  // ── subgrid ───────────────────────────────────────────────────────────────
  get subgrid() { return this.#subgrid; }
  set subgrid(value) {
    const coerced = Boolean(value);
    if (this.#subgrid === coerced) return;
    this.#subgrid = coerced;
    this.toggleAttribute('subgrid', coerced);
    this.requestUpdate();
  }

  // ── lifecycle ─────────────────────────────────────────────────────────────
  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skinName = this.#resolveSkin();
      if (skinName === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        _renderItem(this, true);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        _renderItem(this, false);
      }
    } finally {
      this.#rendering = false;
    }
  }

  #resolveSkin() {
    if (this.#skin) return this.#skin;
    return resolveSkinFromAncestors(this);
  }
}

function _renderItem(host, wireframe) {
  const style = _buildItemStyle(host, wireframe);
  const existing = host.shadowRoot.querySelector('[part="base"]');
  if (existing) {
    existing.setAttribute('style', style);
  } else {
    host.shadowRoot.innerHTML = `<div part="base" style="${style}"><slot></slot></div>`;
  }
}

function _buildItemStyle(host, wireframe) {
  const p = [];

  const gridColumn = host._resolvedGridColumn;
  if (gridColumn) p.push(`grid-column:${gridColumn}`);

  if (host.rowSpan !== '') p.push(`grid-row:span ${host.rowSpan}`);

  if (host.colStart !== '') p.push(`grid-column-start:${host.colStart}`);
  if (host.colEnd !== '')   p.push(`grid-column-end:${host.colEnd}`);
  if (host.rowStart !== '') p.push(`grid-row-start:${host.rowStart}`);
  if (host.rowEnd !== '')   p.push(`grid-row-end:${host.rowEnd}`);

  if (host.area) p.push(`grid-area:${host.area}`);

  if (host.order !== '') p.push(`order:${host.order}`);

  if (host.subgrid) {
    p.push('display:grid');
    p.push('grid-template-columns:subgrid');
  }

  if (wireframe) {
    p.push('outline:1px dashed');
  }

  return p.join(';');
}

if (!customElements.get('run-grid-item')) {
  customElements.define('run-grid-item', RunGridItem);
}

export { RunGridItem };
export default RunGridItem;
